import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { useToast } from '../components/common';
import { changedFields } from './graph-section/graphData';
import { ago } from '../nucleus/format';
import '../styles/Proposals.css';
import DataUpdates from './proposals/DataUpdates';

// ── Design tokens (mirrors HTML mockup) ──────────────────────

const SCHEMA_TYPE_META = {
  FLAG_TENSION:    { label: 'FLAG_TENSION',   cls: 'schema-tension' },
  RATIFY_NODE:     { label: 'Ratify',         cls: 'schema-ratify'  },
  'golden:value':  { label: 'golden:value',   cls: 'schema-value'  },
  'golden:belief': { label: 'golden:belief',  cls: 'schema-belief' },
  'self:pattern':  { label: 'self:pattern',   cls: 'schema-pattern'},
  edge_proposal:   { label: 'edge proposal',  cls: 'schema-edge'   },
};

// One page of the review queue. The view filters across seven tabs on the
// client, so it needs the whole set for its counts to be true — when there is
// more than this, the sidebar says so rather than quietly showing a prefix.
const PAGE_SIZE = 200;

const STATUS_TABS = [
  { key: 'TENSION',            label: 'Tension',  cls: 'danger-tab',  filterFn: p => p.schema_type === 'FLAG_TENSION' && !['ARCHIVED','CANCELLED','REJECTED'].includes(p.status) },
  { key: 'PENDING',            label: 'Pending',  cls: 'warning-tab', filterFn: p => p.status === 'PENDING' && p.schema_type !== 'FLAG_TENSION' },
  { key: 'READY_FOR_APPROVAL', label: 'Ready',    cls: '',            filterFn: p => p.status === 'READY_FOR_APPROVAL' },
  { key: 'DEFERRED',           label: 'Deferred', cls: '',            filterFn: p => p.status === 'DEFERRED' },
  { key: 'APPROVED',           label: 'Approved', cls: '',            filterFn: p => ['APPROVED','EDIT_CONFIRMED'].includes(p.status) },
  { key: 'REJECTED',           label: 'Rejected', cls: '',            filterFn: p => p.status === 'REJECTED' },
  { key: 'ARCHIVED',           label: 'Archived', cls: '',            filterFn: p => ['ARCHIVED','CANCELLED'].includes(p.status) },
];

const BADGE_CLS = {
  PENDING:'badge-pending', READY_FOR_APPROVAL:'badge-ready', APPROVED:'badge-approved',
  EDIT_CONFIRMED:'badge-approved', REJECTED:'badge-rejected', DEFERRED:'badge-deferred',
  CANCELLED:'badge-deferred', ARCHIVED:'badge-archived', APPLIED:'badge-applied', FAILED:'badge-rejected',
};

const PRIORITY_DOT = p => {
  if (p >= 90) return 'priority-tension';
  if (p >= 70) return 'priority-high';
  if (p >= 40) return 'priority-normal';
  return 'priority-low';
};

function SchemaChip({ type, label }) {
  const meta = SCHEMA_TYPE_META[type];
  if (!meta && !label) return null;
  return <span className={`schema-chip ${meta ? meta.cls : 'schema-edge'}`}>{label || meta?.label}</span>;
}

function StatusBadge({ status, label }) {
  const cls = BADGE_CLS[status] || 'badge-pending';
  return <span className={`status-badge ${cls}`}>{label || status?.replace(/_/g, ' ')}</span>;
}

// ── Resolution options for FLAG_TENSION ──────────────────────

const TENSION_OPTIONS = [
  {
    icon: '🔗',
    title: 'Add a tension edge — I hold both, they\'re different domains',
    desc: <>Creates a <code>tension_with</code> edge. You write a note in your own words explaining how you navigate this. Both nodes stay confirmed.</>,
    action: 'edge',
  },
  {
    icon: '✏️',
    title: 'Edit the existing node to include the constraint',
    desc: 'Update the existing confirmed value/belief to qualify it with the new signal. Closes the tension by refining the existing node.',
    action: 'edit',
  },
  {
    icon: '📥',
    title: 'Confirm the proposed value as a separate node',
    desc: 'Existing node stays unchanged. The proposed value becomes its own confirmed node, lateral to the existing one in this domain.',
    action: 'approve',
  },
];

// ── Main Component ────────────────────────────────────────────

const ProposalsView = ({ panelOneMode = false }) => {
  const toast = useToast();
  const [allProposals, setAllProposals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('TENSION');
  // Tension LEADS because it is the urgent kind, but it is usually empty — and
  // an empty first tab reads as a broken page while the real queue sits one tab
  // over. So the opening tab falls through to the first one with anything in it,
  // until you pick one yourself; after that the choice is yours and stays put.
  const [tabPicked, setTabPicked] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit mode
  const [showEditForm, setShowEditForm] = useState(false);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editValueJson, setEditValueJson] = useState({});

  // Reject mode
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Reasoning panel
  const [showReasoning, setShowReasoning] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // ── Data fetching ─────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getProposals({ limit: PAGE_SIZE, offset: 0 });
      const rows = data.proposals || data.items || [];
      setAllProposals(rows);
      // The server knows how many there are; this view used to ask for 200 and
      // silently drop the rest, which also made every tab count below a lie.
      setTotal(typeof data.total === 'number' ? data.total : rows.length);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await apiService.getProposals(
        { limit: PAGE_SIZE, offset: allProposals.length });
      const rows = data.proposals || data.items || [];
      // De-duplicate by id: the ordering is priority DESC, created_at DESC, so a
      // proposal answered while you were reading can shift across the boundary
      // and arrive twice.
      setAllProposals((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...rows.filter((p) => !seen.has(p.id))];
      });
      if (typeof data.total === 'number') setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-select first visible item when data loads or tab changes
  useEffect(() => {
    if (loading || selected) return;
    const tabMeta = STATUS_TABS.find(t => t.key === activeTab);
    const list = allProposals
      .filter(tabMeta?.filterFn ?? (() => true))
      .sort((a, b) => (b.priority || 50) - (a.priority || 50));
    if (list.length > 0) selectProposal(list[0]);
  }, [loading, activeTab, allProposals]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab counts & visible list ────────────────────────────

  const tabCounts = {};
  STATUS_TABS.forEach(t => { tabCounts[t.key] = allProposals.filter(t.filterFn).length; });

  const activeTabMeta = STATUS_TABS.find(t => t.key === activeTab);
  const visibleList = allProposals
    .filter(activeTabMeta?.filterFn ?? (() => true))
    .sort((a, b) => (b.priority || 50) - (a.priority || 50));

  // More on the server than in hand: every count below is a floor, not a total.
  const partial = allProposals.length < total;

  // Open on a tab that has something in it. Only until you pick one — after
  // that an empty tab is a deliberate answer ("nothing rejected"), not a fault.
  useEffect(() => {
    if (loading || tabPicked || visibleList.length > 0) return;
    const firstWithRows = STATUS_TABS.find(t => allProposals.some(t.filterFn));
    if (firstWithRows && firstWithRows.key !== activeTab) setActiveTab(firstWithRows.key);
  }, [loading, tabPicked, allProposals]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection ────────────────────────────────────────────

  const selectProposal = async (p) => {
    setSelected(p);
    setShowRejectForm(false);
    setShowEditForm(false);
    setRejectReason('');
    setShowReasoning(false);
    setLoadingDetail(true);
    try {
      const full = await apiService.getProposal(p.id);
      setSelected(full);
    } catch { /* keep summary */ }
    finally { setLoadingDetail(false); }
  };

  // ── After any action ─────────────────────────────────────

  const afterAction = async () => {
    setSubmitting(false);
    setShowRejectForm(false);
    setShowEditForm(false);
    setRejectReason('');
    setEditText('');
    setEditDescription('');
    setEditValueJson({});
    await fetchAll();
    if (selected) {
      try { const fresh = await apiService.getProposal(selected.id); setSelected(fresh); }
      catch { setSelected(null); }
    }
  };

  // ── Actions ──────────────────────────────────────────────

  const handleApprove = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await apiService.approveProposal(selected.id);
      toast.success('Confirmed');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleApproveAndApply = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await apiService.approveProposal(selected.id);
      await apiService.applyProposal(selected.id);
      toast.success('Confirmed and applied to graph');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleApproveAgentDraft = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await apiService.approveProposal(selected.id);
      toast.success(`${appName} draft approved`);
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleEditConfirm = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      // Only what the person changed: the fields start from what the proposal
      // shows, and sent back untouched the server read them as edits. The
      // proposal's version goes back too, so an app's change since is refused.
      const edited = changedFields(
        { title: selected.title, detail: selected.description },
        { title: editText, detail: editDescription, value: editValueJson, shown: selected.edit_value },
      );
      await apiService.editConfirmProposal(selected.id, {
        edited_content: edited,
        ...(selected.updated_at ? { seen_updated_at: selected.updated_at } : {}),
      });
      toast.success(`Edit confirmed — policy delta sent to ${appName}`);
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim() || submitting) return;
    setSubmitting(true);
    try {
      await apiService.rejectProposal(selected.id, rejectReason);
      toast.success('Proposal rejected');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleDefer = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiService.deferProposal(selected.id);
      toast.success(res.auto_archived ? 'Auto-archived after 3 deferrals' : `Deferred (${res.deferred_count}/3)`);
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleApply = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const result = await apiService.applyProposal(selected.id);
      const nodeCount = result.nodes_created?.length || 0;
      const relCount = result.relationships_created?.length || 0;
      const errors = result.errors || [];
      if (errors.length > 0) {
        toast.error(`Apply failed: ${errors[0]}`);
      } else if (nodeCount === 0 && relCount === 0) {
        toast.error('Applied but nothing was written — proposal may have no actionable data');
      } else {
        const parts = [];
        if (nodeCount > 0) parts.push(`${nodeCount} node${nodeCount !== 1 ? 's' : ''}`);
        if (relCount > 0) parts.push(`${relCount} relationship${relCount !== 1 ? 's' : ''}`);
        toast.success(`Applied to graph — ${parts.join(', ')} created`);
      }
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const openEditForm = () => {
    setEditText(selected?.title || '');
    setEditDescription(selected?.description || '');
    // edit_value is what a value edit replaces, worked out by the server.
    setEditValueJson({ ...(selected?.edit_value || {}) });
    setShowEditForm(true);
    setShowRejectForm(false);
  };

  // ── Derived state ────────────────────────────────────────

  const appName = selected?.app_name || selected?.app_id || 'app';
  const isTension = selected?.schema_type === 'FLAG_TENSION';
  const isDeferred = selected?.status === 'DEFERRED';
  const canAct = selected && ['PENDING', 'READY_FOR_APPROVAL'].includes(selected.status);
  const canActDeferred = selected?.status === 'DEFERRED';
  const canApply = selected && ['APPROVED', 'EDIT_CONFIRMED'].includes(selected.status);
  const isApplied = selected?.status === 'APPLIED';
  const reasoningMeta = selected?.reasoning_metadata || {};

  // Entity from canonical_data
  const canonicalEntities = selected?.canonical_data?.entities || [];
  const firstEntity = canonicalEntities[0] || null;
  const proposedRelationships = selected?.raw_data?.relationships || [];

  // Conflicting nodes for tension
  const existingNode = selected?.canonical_data?.existing_node || selected?.context_data?.existing_node || null;
  const proposedNode = selected?.canonical_data?.proposed_node || selected?.context_data?.proposed_node || null;

  // ── Render ───────────────────────────────────────────────

  return (
    <div className={`pq-shell${panelOneMode ? ' pq-shell--rail' : ''}`}>

      {/* ── SIDEBAR ── */}
      <div className="pq-sidebar">
        <div className="pq-sidebar-header">
          <div className="pq-sidebar-title">Proposals</div>
          <div className="pq-sidebar-sub">Review queue — sorted by priority</div>
        </div>

        <DataUpdates />

        <div className="pq-status-tabs">
          {STATUS_TABS.map(t => (
            <button
              key={t.key}
              className={`btn pq-status-tab${activeTab === t.key ? ' active' : ''}${t.cls ? ' ' + t.cls : ''}`}
              onClick={() => { setTabPicked(true); setActiveTab(t.key); setSelected(null); setShowEditForm(false); setShowRejectForm(false); }}
            >
              {t.label}
              {tabCounts[t.key] > 0 && (
                <span className="pq-tab-count">{tabCounts[t.key]}{partial ? '+' : ''}</span>
              )}
            </button>
          ))}
        </div>

        <div className="pq-item-list">
          {loading ? (
            <div className="pq-loading">Loading…</div>
          ) : visibleList.length === 0 ? (
            <div className="pq-empty-list">Nothing here</div>
          ) : visibleList.map(p => {
            const isEditing = selected?.id === p.id && showEditForm;
            const deferredTwicePlus = p.deferred_count >= 2 && p.status === 'DEFERRED';
            const deferredOnce = p.deferred_count === 1 && p.status === 'DEFERRED';
            return (
              <div
                key={p.id}
                className={`pq-list-item${p.schema_type === 'FLAG_TENSION' ? ' tension' : ''}${selected?.id === p.id ? ' selected' : ''}${deferredTwicePlus ? ' deferred-resurface' : ''}`}
                onClick={() => selectProposal(p)}
              >
                <div className="pq-item-top">
                  <div className="pq-item-title-row">
                    <div className={`pq-priority-dot ${PRIORITY_DOT(p.priority || 50)}`} />
                    <div className="pq-item-title">{p.title}</div>
                  </div>
                </div>
                <div className="pq-item-meta">
                  {p.schema_type && <SchemaChip type={p.schema_type} />}
                  {p.status === 'DEFERRED' && p.deferred_count > 0 && (
                    <StatusBadge status="DEFERRED" label={`DEFERRED ×${p.deferred_count}`} />
                  )}
                  {isEditing && <span className="pq-editing-note">editing…</span>}
                  {!isEditing && !p.deferred_count && <span className="pq-item-time">{ago(p.created_at)}</span>}
                </div>
                {deferredTwicePlus && (
                  <div className="pq-deferred-note">
                    <strong>Re-surfaced</strong> — deferred twice, proposed {ago(p.created_at)}
                  </div>
                )}
                {deferredOnce && (
                  <div className="pq-deferred-note">Deferred {ago(p.updated_at)}</div>
                )}
                {p.app_id && (
                  <div className="pq-deferred-note">via {p.app_name || p.app_id}</div>
                )}
              </div>
            );
          })}

          {!loading && partial && (
            <div className="pq-more">
              <span className="pq-more-note">
                Showing {allProposals.length} of {total}
              </span>
              <button
                className="btn pq-more-btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── DETAIL PANEL ── (inline panel normally; a centered modal in rail mode) */}
      {(!panelOneMode || selected) && (
      <>
      {panelOneMode && <div className="pq-detail-backdrop" onClick={() => setSelected(null)} />}
      <div className={`pq-detail-panel${panelOneMode ? ' pq-detail-panel--modal' : ''}`}>
        {panelOneMode && selected && (
          <button type="button" className="btn pq-detail-close" onClick={() => setSelected(null)} aria-label="Close">&times;</button>
        )}
        {!selected ? (
          <div className="pq-empty-state">
            <div className="pq-empty-icon">◈</div>
            <div className="pq-empty-label">Select a proposal to review</div>
            <div className="pq-empty-sub">Sorted by priority — tensions first</div>
          </div>
        ) : (
          <>
            {/* ── TOP BAR ── */}
            <div className="pq-detail-topbar">
              <div className="pq-detail-topbar-main">
                <div className="pq-detail-title">{selected.title}</div>
                <div className="pq-detail-meta-row">
                  <StatusBadge status={selected.status} label={
                    selected.status === 'DEFERRED' && selected.deferred_count
                      ? `DEFERRED ×${selected.deferred_count}`
                      : isTension ? 'PENDING RESOLUTION' : undefined
                  } />
                  {selected.schema_type && <SchemaChip type={selected.schema_type} />}
                  {showEditForm && <span className="pq-edit-mode-badge">✏ Edit mode active</span>}
                  {selected.app_id && (
                    <span className="pq-detail-source">via {selected.app_name || selected.app_id} · {ago(selected.created_at)}</span>
                  )}
                </div>
              </div>

              <div className="pq-action-bar">
                {showEditForm ? (
                  <button className="btn pq-btn pq-btn-ghost" onClick={() => setShowEditForm(false)}>Cancel edit</button>
                ) : showRejectForm ? (
                  <button className="btn pq-btn pq-btn-ghost" onClick={() => setShowRejectForm(false)}>Cancel</button>
                ) : isTension && (canAct || canActDeferred) ? (
                  <>
                    <button className="btn pq-btn pq-btn-primary" disabled={submitting} onClick={handleApproveAndApply}>Resolve Tension</button>
                    <button className="btn pq-btn pq-btn-defer" disabled={submitting} onClick={handleDefer}>Defer</button>
                  </>
                ) : canAct ? (
                  <>
                    <button className="btn pq-btn pq-btn-primary" disabled={submitting} onClick={handleApprove}>Confirm</button>
                    <button className="btn pq-btn pq-btn-edit" onClick={openEditForm}>✏ Edit + Confirm</button>
                    <button className="btn pq-btn pq-btn-defer" disabled={submitting} onClick={handleDefer}>Defer</button>
                    <button className="btn pq-btn pq-btn-danger" onClick={() => { setShowRejectForm(true); setShowEditForm(false); }}>Reject</button>
                  </>
                ) : canActDeferred ? (
                  <>
                    <button className="btn pq-btn pq-btn-primary" disabled={submitting} onClick={handleApprove}>Confirm</button>
                    <button className="btn pq-btn pq-btn-edit" onClick={openEditForm}>✏ Edit + Confirm</button>
                    <button className="btn pq-btn pq-btn-danger" onClick={() => { setShowRejectForm(true); setShowEditForm(false); }}>Reject</button>
                    <button className="btn pq-btn pq-btn-defer" disabled={submitting} onClick={handleDefer}>Defer Again</button>
                  </>
                ) : canApply ? (
                  <button className="btn pq-btn pq-btn-primary" disabled={submitting} onClick={handleApply}>↳ Apply to Graph</button>
                ) : null}
              </div>
            </div>

            {/* ── DETAIL BODY ── */}
            <div className="pq-detail-body">
              {loadingDetail && <div className="pq-loading">Loading…</div>}

              {/* Deferred nudge (2×) */}
              {isDeferred && selected.deferred_count >= 2 && (
                <div className="pq-nudge-card">
                  <div className="pq-nudge-label">You've deferred this {selected.deferred_count === 2 ? 'twice' : `${selected.deferred_count}×`}</div>
                  <div className="pq-nudge-text">
                    Still relevant? If you defer a third time this will be archived — you can always retrieve it, but it will stop re-surfacing.
                  </div>
                </div>
              )}

              {/* Tension banner */}
              {isTension && (
                <div className="pq-tension-banner">
                  <div className="pq-tension-icon">⚡</div>
                  <div>
                    <div className="pq-tension-label">Tension flagged — resolve before new proposals in this domain</div>
                    <div className="pq-tension-text">{selected.description}</div>
                  </div>
                </div>
              )}

              {/* Steering question */}
              {selected.steering_question && (
                <div className="pq-steering-card">
                  <div className="pq-steering-label">Steering question</div>
                  <div className="pq-steering-text">"{selected.steering_question}"</div>
                </div>
              )}

              {/* Edit form */}
              {showEditForm && (
                <>
                  <div className="pq-edit-area">
                    <div className="pq-edit-label">Edit {appName}'s draft — your words replace theirs</div>
                    <div className="pq-edit-original">{selected.title}</div>
                    <input
                      className="pq-edit-input-title"
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      placeholder="Your version of the title…"
                    />
                    {selected.description && !firstEntity?.value_json && (
                      <>
                        <div className="pq-edit-original" style={{ marginTop: 8 }}>{selected.description}</div>
                        <textarea
                          className="pq-edit-input"
                          value={editDescription}
                          onChange={e => setEditDescription(e.target.value)}
                          placeholder="Your version of the description…"
                          rows={3}
                        />
                      </>
                    )}
                    {Object.keys(editValueJson).length > 0 && (
                      <div className="pq-vj-edit-block">
                        {Object.entries(editValueJson).map(([k, v]) => {
                          const isLong = typeof v === 'string' && v.length > 80;
                          const displayVal = Array.isArray(v) ? v.join(', ') : String(v ?? '');
                          return (
                            <div key={k} className="pq-vj-edit-row">
                              <label className="pq-vj-edit-label">{k.replace(/_/g, ' ')}</label>
                              {isLong ? (
                                <textarea
                                  className="pq-edit-input"
                                  value={displayVal}
                                  onChange={e => setEditValueJson(prev => ({ ...prev, [k]: e.target.value }))}
                                  rows={3}
                                />
                              ) : (
                                <input
                                  className="pq-edit-input-title"
                                  value={displayVal}
                                  onChange={e => setEditValueJson(prev => ({ ...prev, [k]: e.target.value }))}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="pq-edit-hint">
                      The difference between your wording and {appName}'s draft is saved as a learning signal. Your framing will be used in future proposals.
                    </div>
                    <div className="pq-edit-confirm-bar">
                      <button className="btn pq-btn pq-btn-amber-solid" disabled={submitting} onClick={handleEditConfirm}>
                        Confirm with my edit
                      </button>
                      <button className="btn pq-btn pq-btn-ghost" disabled={submitting} onClick={handleApproveAgentDraft}>
                        Approve {appName} draft instead
                      </button>
                      <button
                        className="btn pq-btn pq-btn-danger"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => { setShowEditForm(false); setShowRejectForm(true); }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>

                  {/* Delta teaches panel — visible while editing */}
                  <div className="pq-reasoning-panel pq-reasoning-amber">
                    <div className="pq-reasoning-toggle" style={{ cursor: 'default' }}>
                      <span className="pq-section-label" style={{ margin: 0, color: 'var(--amber)' }}>
                        What the edit delta teaches {appName}
                      </span>
                    </div>
                    <div className="pq-reasoning-content">
                      {/* Title diff */}
                      <div className="pq-reasoning-row">
                        <span className="pq-reasoning-key">{appName} title</span>
                        <span className="pq-reasoning-val" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>"{selected.title}"</span>
                      </div>
                      <div className="pq-reasoning-row">
                        <span className="pq-reasoning-key">your title</span>
                        <span className="pq-reasoning-val" style={{ fontWeight: editText && editText !== selected.title ? 500 : 400, color: editText && editText !== selected.title ? 'inherit' : 'var(--text-muted)' }}>
                          {editText && editText !== selected.title ? `"${editText}"` : '(unchanged)'}
                        </span>
                      </div>
                      {/* Description diff — only show if there is a description to edit */}
                      {selected.description && (
                        <>
                          <div className="pq-reasoning-row" style={{ marginTop: 6 }}>
                            <span className="pq-reasoning-key">{appName} desc</span>
                            <span className="pq-reasoning-val" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>"{selected.description}"</span>
                          </div>
                          <div className="pq-reasoning-row">
                            <span className="pq-reasoning-key">your desc</span>
                            <span className="pq-reasoning-val" style={{ fontWeight: editDescription && editDescription !== selected.description ? 500 : 400, color: editDescription && editDescription !== selected.description ? 'inherit' : 'var(--text-muted)' }}>
                              {editDescription && editDescription !== selected.description ? `"${editDescription}"` : '(unchanged)'}
                            </span>
                          </div>
                        </>
                      )}
                      {/* value_json field diffs */}
                      {Object.entries(editValueJson).map(([k, v]) => {
                        const original = selected.edit_value?.[k];
                        const changed = String(v) !== String(original ?? '');
                        return (
                          <div key={k} className="pq-reasoning-row" style={{ marginTop: 4 }}>
                            <span className="pq-reasoning-key">{k.replace(/_/g, ' ')}</span>
                            <span className="pq-reasoning-val" style={{ fontWeight: changed ? 500 : 400, color: changed ? 'inherit' : 'var(--text-muted)' }}>
                              {changed ? `"${v}"` : '(unchanged)'}
                            </span>
                          </div>
                        );
                      })}
                      <div className="pq-reasoning-row" style={{ marginTop: 6 }}>
                        <span className="pq-reasoning-key">scope</span>
                        <span className="pq-reasoning-val">Shapes future proposals in this user's identity graph only.</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Edit delta (post-confirm, read-only) */}
              {selected.status === 'EDIT_CONFIRMED' && selected.edit_delta && !showEditForm && (() => {
                const d = selected.edit_delta;
                const titleChanged = d.original_title && d.edited_title && d.original_title !== d.edited_title;
                const descChanged = d.original_description && d.edited_description && d.original_description !== d.edited_description;
                return (
                  <div className="pq-edit-area">
                    <div className="pq-edit-label">Policy delta — sent to {appName}</div>
                    {titleChanged && (
                      <>
                        <div className="pq-delta-row">
                          <span className="pq-delta-key">{appName} said</span>
                          <span className="pq-delta-val" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>"{d.original_title}"</span>
                        </div>
                        <div className="pq-delta-row">
                          <span className="pq-delta-key">you wrote</span>
                          <span className="pq-delta-val pq-delta-new">"{d.edited_title}"</span>
                        </div>
                      </>
                    )}
                    {descChanged && (
                      <>
                        <div className="pq-delta-row" style={{ marginTop: titleChanged ? 8 : 0 }}>
                          <span className="pq-delta-key">{appName} desc</span>
                          <span className="pq-delta-val" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>"{d.original_description}"</span>
                        </div>
                        <div className="pq-delta-row">
                          <span className="pq-delta-key">you wrote</span>
                          <span className="pq-delta-val pq-delta-new">"{d.edited_description}"</span>
                        </div>
                      </>
                    )}
                    {!titleChanged && !descChanged && (
                      <div className="pq-delta-row">
                        <span className="pq-delta-key">no changes</span>
                        <span className="pq-delta-val" style={{ color: 'var(--text-muted)' }}>You approved {appName}'s draft as-is</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Reject form */}
              {showRejectForm && (
                <div className="pq-reject-form">
                  <div className="pq-reject-label">Rejection reason</div>
                  <textarea
                    className="pq-reject-input"
                    placeholder="Why are you rejecting this proposal?"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                  />
                  <div className="pq-reject-hint">Required — this becomes a learning signal for {appName}.</div>
                  <div className="pq-edit-confirm-bar" style={{ marginTop: 10 }}>
                    <button className="btn pq-btn pq-btn-danger" disabled={submitting || !rejectReason.trim()} onClick={handleReject}>
                      Confirm rejection
                    </button>
                    <button className="btn pq-btn pq-btn-ghost" onClick={() => setShowRejectForm(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Rejection reason (view) */}
              {selected.status === 'REJECTED' && selected.rejection_reason && !showRejectForm && (
                <div className="pq-reject-form">
                  <div className="pq-reject-label">Rejection reason</div>
                  <div className="pq-reject-reason-text">{selected.rejection_reason}</div>
                </div>
              )}

              {/* ── TENSION: conflicting nodes + resolution options ── */}
              {isTension && !showEditForm && !showRejectForm && (
                <>
                  {/* Conflicting nodes grid */}
                  <div>
                    <div className="pq-section-label">Conflicting nodes</div>
                    <div className="pq-conflict-grid">
                      {/* Existing node */}
                      <div className="pq-entity-card">
                        <div className="pq-entity-title-row">
                          <SchemaChip
                            type={existingNode?.schema_type || 'golden:value'}
                            label={existingNode?.schema_type ? SCHEMA_TYPE_META[existingNode.schema_type]?.label : 'golden:value'}
                          />
                          <span className="pq-entity-name">{existingNode?.title || existingNode?.id || 'Existing node'}</span>
                        </div>
                        {existingNode?.description && (
                          <div className="pq-entity-desc">{existingNode.description}</div>
                        )}
                        {!existingNode && selected.context_data?.tension_node_ids?.length > 0 && (
                          <div className="pq-entity-desc">{selected.context_data.tension_node_ids[0]}</div>
                        )}
                      </div>
                      {/* Proposed node */}
                      <div className="pq-entity-card pq-entity-card-proposed">
                        <div className="pq-entity-title-row">
                          <SchemaChip type={null} label="proposed" />
                          <span className="pq-entity-name">{proposedNode?.title || 'draft'}</span>
                        </div>
                        {proposedNode?.description && (
                          <div className="pq-entity-desc">{proposedNode.description}</div>
                        )}
                        {!proposedNode && selected.description && (
                          <div className="pq-entity-desc">{selected.description}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resolution options */}
                  <div>
                    <div className="pq-section-label">How to resolve</div>
                    <div className="pq-resolution-list">
                      {TENSION_OPTIONS.map((opt, i) => (
                        <div
                          key={i}
                          className="pq-resolution-option"
                          onClick={() => {
                            if (opt.action === 'edit') openEditForm();
                            else if (!submitting) handleApproveAndApply();
                          }}
                        >
                          <span className="pq-resolution-icon">{opt.icon}</span>
                          <div>
                            <div className="pq-resolution-title">{opt.title}</div>
                            <div className="pq-resolution-desc">{opt.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── NON-TENSION: proposed node + reasoning ── */}
              {!isTension && !showEditForm && (
                <>
                  {/* Description — hide when value_json is present (structured fields replace it) */}
                  {(selected.canonical_data?.description || selected.description) && !firstEntity?.value_json && (
                    <div>
                      <div className="pq-section-label">Description</div>
                      <div className="pq-description-text">{selected.canonical_data?.description || selected.description}</div>
                    </div>
                  )}

                  {/* Proposed node(s) entity cards — one card per entity */}
                  {(canonicalEntities.length > 0 || selected.title) && (
                    <div>
                      <div className="pq-section-label">
                        {canonicalEntities.length > 1 ? `Proposed nodes (${canonicalEntities.length})` : 'Proposed node'}
                      </div>
                      {(canonicalEntities.length > 0 ? canonicalEntities : [null]).map((entity, idx) => (
                        <div key={idx} className="pq-entity-card" style={{ marginBottom: idx < canonicalEntities.length - 1 ? 8 : 0 }}>
                          <div className="pq-entity-title-row">
                            <SchemaChip type={entity?.entity_type || selected.schema_type} />
                            <span className="pq-entity-name">
                              {entity?.title || entity?.name || selected.title}
                            </span>
                          </div>
                          {(entity?.description || entity?.value) && !entity?.value_json && (
                            <div className="pq-entity-desc">{entity.description || entity.value}</div>
                          )}
                          {entity?.value_json && Object.keys(entity.value_json).length > 0 && (
                            <div className="pq-entity-value-json">
                              {Object.entries(entity.value_json)
                                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                                .map(([k, v]) => (
                                  <div key={k} className="pq-reasoning-row">
                                    <span className="pq-reasoning-key">{k.replace(/_/g, ' ')}</span>
                                    <span className="pq-reasoning-val">
                                      {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reasoning panel */}
                  {Object.keys(reasoningMeta).length > 0 && (
                    <div className="pq-reasoning-panel">
                      <div className="pq-reasoning-toggle" onClick={() => setShowReasoning(v => !v)}>
                        <span className="pq-section-label" style={{ margin: 0 }}>{appName} reasoning</span>
                        <span className="pq-reasoning-chevron">{showReasoning ? '▲ collapse' : '▼ expand'}</span>
                      </div>
                      {showReasoning && (
                        <div className="pq-reasoning-content">
                          {Object.entries(reasoningMeta).map(([k, v]) => (
                            <div key={k} className="pq-reasoning-row">
                              <span className="pq-reasoning-key">{k}</span>
                              <span className="pq-reasoning-val">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Proposed relationships */}
                  {proposedRelationships.length > 0 && (
                    <div>
                      <div className="pq-section-label">Proposed relationships</div>
                      {proposedRelationships.map((rel, i) => (
                        <div key={i} className="pq-entity-card" style={{ marginBottom: 6 }}>
                          <div className="pq-reasoning-row" style={{ borderTop: 'none', paddingTop: 0 }}>
                            <span className="pq-reasoning-key">from</span>
                            <span className="pq-reasoning-val">{rel.from || rel.from_node_id || '—'}</span>
                          </div>
                          <div className="pq-reasoning-row">
                            <span className="pq-reasoning-key">type</span>
                            <span className="pq-reasoning-val" style={{ fontWeight: 500 }}>{rel.relationship_type || rel.edge_type || '—'}</span>
                          </div>
                          <div className="pq-reasoning-row">
                            <span className="pq-reasoning-key">to</span>
                            <span className="pq-reasoning-val">{rel.to || rel.to_node_id || '—'}</span>
                          </div>
                          {(rel.edge_note || rel.context) && (
                            <div className="pq-reasoning-row">
                              <span className="pq-reasoning-key">note</span>
                              <span className="pq-reasoning-val">{rel.edge_note || rel.context}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Source signals */}
                  {selected.source_signal_ids?.length > 0 && (
                    <div>
                      <div className="pq-section-label">Source signals</div>
                      <div className="pq-entity-tags">
                        {selected.source_signal_ids.map((s, i) => (
                          <span key={i} className="pq-tag-pill pq-tag-signal">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Applied status */}
                  {isApplied && (
                    <div className="pq-linked-proposal" style={{ background: 'var(--success-light)', borderColor: 'rgba(90,122,90,0.2)' }}>
                      <span className="pq-linked-icon" style={{ color: 'var(--success)' }}>✓</span>
                      <div>
                        <div className="pq-linked-label" style={{ color: 'var(--success)' }}>Applied to graph</div>
                        <div className="pq-linked-title" style={{ color: 'var(--success)' }}>Node written to identity graph</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default ProposalsView;
